import React, { useEffect, useMemo, useState } from 'react';
import { Button } from '@mui/material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import RestartAltRoundedIcon from '@mui/icons-material/RestartAltRounded';
import SaveRoundedIcon from '@mui/icons-material/SaveRounded';
import { API_BASE_URL, fetchJson } from './api';
import MonthCalendar from './MonthCalendar';
import SiteVisitDialog from './SiteVisitDialog';

const CALENDAR_UI_PREFS_KEY = 'monsurv_calendar_ui_prefs_v1';

function loadCalendarUiPrefs() {
    const defaults = {
        calendarView: 'month',
        visibleCalendarSections: {
            personal: true,
            team: true,
            due: true
        }
    };

    try {
        const raw = localStorage.getItem(CALENDAR_UI_PREFS_KEY);
        const parsed = raw ? JSON.parse(raw) : null;
        if (!parsed || typeof parsed !== 'object') return defaults;

        const view = String(parsed.calendarView || '').trim().toLowerCase();
        const safeView = ['day', 'week', 'month'].includes(view) ? view : 'month';

        const sections = parsed.visibleCalendarSections && typeof parsed.visibleCalendarSections === 'object'
            ? parsed.visibleCalendarSections
            : {};

        return {
            calendarView: safeView,
            visibleCalendarSections: {
                personal: sections.personal !== false,
                team: sections.team !== false,
                due: sections.due !== false
            }
        };
    } catch {
        return defaults;
    }
}

function startOfWeekMonday(d) {
    const x = new Date(d);
    x.setHours(0, 0, 0, 0);
    const day = x.getDay();
    const diff = (day === 0 ? -6 : 1) - day;
    x.setDate(x.getDate() + diff);
    return x;
}

function firstOfMonthLocal(d) {
    const x = new Date(d);
    x.setDate(1);
    x.setHours(0, 0, 0, 0);
    return x;
}

function startOfDayLocal(d) {
    const x = new Date(d);
    x.setHours(0, 0, 0, 0);
    return x;
}

function addDays(d, n) {
    const x = new Date(d);
    x.setDate(x.getDate() + n);
    return startOfDayLocal(x);
}

function addMonths(d, n) {
    const x = new Date(d);
    x.setMonth(x.getMonth() + n);
    return startOfDayLocal(x);
}

function formatLocalDateParam(d) {
    const value = d instanceof Date ? d : new Date(d);
    if (!Number.isFinite(value.getTime())) return '';
    const year = value.getFullYear();
    const month = String(value.getMonth() + 1).padStart(2, '0');
    const day = String(value.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
}

function getCalendarWindow(viewMode, anchorDate) {
    const view = String(viewMode || 'month').trim().toLowerCase();
    const anchor = startOfDayLocal(anchorDate || new Date());

    if (view === 'day') {
        return { start: anchor, days: 1 };
    }

    if (view === 'week') {
        return { start: startOfWeekMonday(anchor), days: 7 };
    }

    const first = firstOfMonthLocal(anchor);
    return { start: startOfWeekMonday(first), days: 42 };
}

function formatTimeRange(startIso, endIso, isAllDay) {
    if (isAllDay) return 'All day';
    try {
        const s = new Date(startIso);
        const e = new Date(endIso);
        const fmt = new Intl.DateTimeFormat('en-GB', { hour: '2-digit', minute: '2-digit' });
        return `${fmt.format(s)}–${fmt.format(e)}`;
    } catch {
        return 'Timed event';
    }
}


export default function Calendar({ isAdmin, authUser }) {
    const calendarUiPrefs = loadCalendarUiPrefs();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [sla, setSla] = useState(null);

    const [rulesLoading, setRulesLoading] = useState(false);
    const [rulesSaving, setRulesSaving] = useState(false);
    const [rulesError, setRulesError] = useState('');
    const [rules, setRules] = useState(null);
    const [rulesDraft, setRulesDraft] = useState(null);

    const [trackingLoading, setTrackingLoading] = useState(false);
    const [trackingError, setTrackingError] = useState('');
    const [tracking, setTracking] = useState(null);

    const [calendarDate, setCalendarDate] = useState(() => startOfDayLocal(new Date()));
    const [calendarView, setCalendarView] = useState(() => calendarUiPrefs.calendarView);
    const [visibleCalendarSections, setVisibleCalendarSections] = useState(() => calendarUiPrefs.visibleCalendarSections);

    const [teamLoading, setTeamLoading] = useState(false);
    const [teamError, setTeamError] = useState('');
    const [teamEvents, setTeamEvents] = useState([]);

    const [ownLoading, setOwnLoading] = useState(false);
    const [ownError, setOwnError] = useState('');
    const [ownEvents, setOwnEvents] = useState([]);
    const [ownCalendarStatus, setOwnCalendarStatus] = useState({ connected: false, provider: null, email: null });
    const [reminderSync, setReminderSync] = useState(null);
    const [projects, setProjects] = useState([]);
    const [siteVisitOpen, setSiteVisitOpen] = useState(false);
    const [selectedAppointment, setSelectedAppointment] = useState(null);

    const personalCalendarLabel = (() => {
        const displayName = String(authUser?.display_name || authUser?.name || authUser?.username || '').trim();
        const firstName = displayName ? displayName.split(/\s+/).filter(Boolean)[0] : '';
        return firstName ? `${firstName}'s Calendar` : 'My Calendar';
    })();

    function formatDate(d) {
        if (!d) return '—';
        const dt = new Date(d);
        if (!Number.isFinite(dt.getTime())) return '—';
        return dt.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    }

    function statusChip(status) {
        const s = String(status || '').toLowerCase();
        const cfg = {
            overdue: { bg: '#fee2e2', fg: '#7f1d1d', label: 'Overdue' },
            due_soon: { bg: '#ffedd5', fg: '#7c2d12', label: 'Due soon' },
            on_track: { bg: '#e0f2fe', fg: '#0c4a6e', label: 'On track' },
            met: { bg: '#dcfce7', fg: '#14532d', label: 'Met' },
            late: { bg: '#fde68a', fg: '#78350f', label: 'Late' },
            paused: { bg: '#e0e7ff', fg: '#3730a3', label: 'Paused' },
            scheduled: { bg: '#f1f5f9', fg: '#334155', label: 'Scheduled' },
            booking_required: { bg: '#ffedd5', fg: '#9a3412', label: 'Booking required' }
        }[s] || { bg: 'rgba(15, 23, 42, 0.06)', fg: '#0f172a', label: status || '—' };

        return (
            <span style={{
                display: 'inline-flex',
                alignItems: 'center',
                padding: '3px 8px',
                borderRadius: 999,
                background: cfg.bg,
                color: cfg.fg,
                fontSize: 12,
                fontWeight: 650,
                whiteSpace: 'nowrap'
            }}>
                {cfg.label}
            </span>
        );
    }

    async function loadSla() {
        if (!isAdmin) return;
        setLoading(true);
        setError('');
        try {
            const token = localStorage.getItem('monsurv_auth_token') || '';
            const json = await fetchJson(`${API_BASE_URL}/admin/settings/sla`, {
                method: 'GET',
                headers: { Authorization: `Bearer ${token}` }
            });
            setSla(json?.sla || null);
        } catch (e) {
            setSla(null);
            setError(e?.message || 'Failed to load SLA settings');
        } finally {
            setLoading(false);
        }
    }

    async function loadCalendarRules() {
        setRulesLoading(true);
        setRulesError('');
        try {
            const token = localStorage.getItem('monsurv_auth_token') || '';
            const json = await fetchJson(`${API_BASE_URL}/api/settings/calendar-rules`, {
                method: 'GET',
                headers: { Authorization: `Bearer ${token}` }
            });
            const r = json?.rules || null;
            setRules(r);
            setRulesDraft(r);
        } catch (e) {
            setRules(null);
            setRulesDraft(null);
            setRulesError(e?.message || 'Failed to load calendar rules');
        } finally {
            setRulesLoading(false);
        }
    }

    async function saveCalendarRules(next) {
        if (!isAdmin) return;
        setRulesSaving(true);
        setRulesError('');
        try {
            const token = localStorage.getItem('monsurv_auth_token') || '';
            const json = await fetchJson(`${API_BASE_URL}/admin/settings/calendar-rules`, {
                method: 'PUT',
                headers: { Authorization: `Bearer ${token}` },
                body: JSON.stringify(next || {})
            });
            const r = json?.rules || null;
            setRules(r);
            setRulesDraft(r);
        } catch (e) {
            setRulesError(e?.message || 'Failed to save calendar rules');
        } finally {
            setRulesSaving(false);
        }
    }

    useEffect(() => {
        loadSla();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAdmin]);

    useEffect(() => {
        loadCalendarRules();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        let cancelled = false;
        fetchJson(`${API_BASE_URL}/key_project_data`, { method: 'GET' })
            .then((data) => {
                if (cancelled) return;
                const rows = Array.isArray(data) ? data : [];
                if (isAdmin) {
                    setProjects(rows);
                    return;
                }
                const identities = [authUser?.username, authUser?.email, authUser?.display_name, authUser?.name]
                    .map((value) => String(value || '').trim().toLowerCase())
                    .filter(Boolean);
                setProjects(rows.filter((project) => identities.includes(String(project?.lead_surveyor || '').trim().toLowerCase())));
            })
            .catch(() => {
                if (!cancelled) setProjects([]);
            });
        return () => { cancelled = true; };
    }, [authUser, isAdmin]);

    async function loadTracking() {
        setTrackingLoading(true);
        setTrackingError('');
        try {
            const token = localStorage.getItem('monsurv_auth_token') || '';
            const window = getCalendarWindow(calendarView, calendarDate);
            const trackingStart = formatLocalDateParam(window.start);

            const json = await fetchJson(`${API_BASE_URL}/api/sla/tracking?start=${encodeURIComponent(trackingStart)}&days=${window.days}&mode=business`, {
                method: 'GET',
                headers: { Authorization: `Bearer ${token}` }
            });
            setTracking(json || null);
        } catch (e) {
            setTracking(null);
            setTrackingError(e?.message || 'Failed to load SLA tracking');
        } finally {
            setTrackingLoading(false);
        }
    }

    async function loadTeamEvents() {
        setTeamLoading(true);
        setTeamError('');
        try {
            const token = localStorage.getItem('monsurv_auth_token') || '';
            const window = getCalendarWindow(calendarView, calendarDate);

            const json = await fetchJson(`${API_BASE_URL}/api/calendar/team?start=${encodeURIComponent(window.start.toISOString())}&days=${window.days}`, {
                method: 'GET',
                headers: { Authorization: `Bearer ${token}` }
            });
            setTeamEvents(Array.isArray(json?.events) ? json.events : []);
        } catch (e) {
            setTeamEvents([]);
            setTeamError(e?.message || 'Failed to load team calendar');
        } finally {
            setTeamLoading(false);
        }
    }

    async function loadOwnCalendarEvents() {
        setOwnLoading(true);
        setOwnError('');
        try {
            const token = localStorage.getItem('monsurv_auth_token') || '';
            const window = getCalendarWindow(calendarView, calendarDate);

            const json = await fetchJson(`${API_BASE_URL}/api/calendar/me?start=${encodeURIComponent(window.start.toISOString())}&days=${window.days}`, {
                method: 'GET',
                headers: { Authorization: `Bearer ${token}` }
            });

            setOwnCalendarStatus({
                connected: Boolean(json?.connected),
                provider: json?.status?.provider || null,
                email: json?.status?.email || null
            });
            setOwnEvents(Array.isArray(json?.events) ? json.events : []);
            setReminderSync(json?.reminder_sync || null);
        } catch (e) {
            setOwnEvents([]);
            setReminderSync(null);
            setOwnCalendarStatus({ connected: false, provider: null, email: null });
            setOwnError(e?.message || 'Failed to load your linked calendar');
        } finally {
            setOwnLoading(false);
        }
    }

    useEffect(() => {
        loadTracking();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [calendarDate, calendarView]);

    useEffect(() => {
        loadTeamEvents();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [calendarDate, calendarView]);

    useEffect(() => {
        loadOwnCalendarEvents();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [calendarDate, calendarView]);

    useEffect(() => {
        try {
            localStorage.setItem(CALENDAR_UI_PREFS_KEY, JSON.stringify({
                calendarView,
                visibleCalendarSections
            }));
        } catch {
            // ignore
        }
    }, [calendarView, visibleCalendarSections]);

    const effectiveRules = rules || {
        working_days: [1, 2, 3, 4, 5],
        business_hours: { start: '09:00', end: '17:30' },
        holidays: [],
        sla_deadlines_mode: 'calendar',
        reminders: { enabled: true, day_offsets: [14, 7, 3, 1, 0], time: '09:00', duration_minutes: 30, sync_window_days: 120 }
    };
    const trackingItems = useMemo(
        () => (Array.isArray(tracking?.items) ? tracking.items : []).filter((item) => (
            item?.action_type === 'prepare_report' || item?.action_type === 'book_site_visit'
        )),
        [tracking?.items]
    );
    const bookingProjects = useMemo(
        () => [...projects].sort((left, right) => {
            const leftInterim = String(left?.in_progress_report_type || '').toLowerCase() === 'interim';
            const rightInterim = String(right?.in_progress_report_type || '').toLowerCase() === 'interim';
            if (leftInterim !== rightInterim) return leftInterim ? -1 : 1;
            return String(left?.reference || '').localeCompare(String(right?.reference || ''), undefined, { numeric: true });
        }),
        [projects]
    );

    return (
        <div>
            <MonthCalendar
                monthDate={calendarDate}
                viewMode={calendarView}
                onChangeView={setCalendarView}
                visibleSections={visibleCalendarSections}
                onToggleSectionVisibility={(sectionKey) => {
                    setVisibleCalendarSections((prev) => ({
                        ...prev,
                        [sectionKey]: !prev?.[sectionKey]
                    }));
                }}
                onPrevMonth={() => {
                    setCalendarDate((d) => {
                        if (calendarView === 'day') return addDays(d, -1);
                        if (calendarView === 'week') return addDays(d, -7);
                        return addMonths(d, -1);
                    });
                }}
                onNextMonth={() => {
                    setCalendarDate((d) => {
                        if (calendarView === 'day') return addDays(d, 1);
                        if (calendarView === 'week') return addDays(d, 7);
                        return addMonths(d, 1);
                    });
                }}
                onToday={() => setCalendarDate(startOfDayLocal(new Date()))}
                workingDays={effectiveRules?.working_days}
                holidays={effectiveRules?.holidays}
                personalEvents={ownEvents}
                teamEvents={teamEvents}
                slaItems={trackingItems}
                statusChip={statusChip}
                personalCalendarLabel={personalCalendarLabel}
                onSelectSiteVisit={(event) => {
                    setSelectedAppointment(event?.appointment || null);
                    setSiteVisitOpen(true);
                }}
            />

            <div className="card" style={{ marginBottom: 16, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
                <div>
                    <div style={{ fontWeight: 700 }}>Site Visits</div>
                    <div style={{ marginTop: 4, fontSize: 13, color: 'var(--muted)' }}>
                        Book a project visit or select an existing Monsurv visit in the calendar to change it.
                    </div>
                </div>
                <Button
                    variant="contained"
                    startIcon={<CalendarMonthRoundedIcon />}
                    onClick={() => {
                        setSelectedAppointment(null);
                        setSiteVisitOpen(true);
                    }}
                    disabled={!bookingProjects.length}
                >
                    Book Site Visit
                </Button>
            </div>

            <SiteVisitDialog
                open={siteVisitOpen}
                onClose={() => setSiteVisitOpen(false)}
                onSaved={() => {
                    loadOwnCalendarEvents();
                    loadTracking();
                }}
                projects={bookingProjects}
                initialProjectReference={selectedAppointment?.project_reference || ''}
                initialAppointment={selectedAppointment}
                isAdmin={isAdmin}
                source="calendar"
            />

            {ownError ? (
                <div style={{ marginBottom: 16, padding: 10, borderRadius: 8, background: '#fee2e2', color: '#7f1d1d' }}>
                    {ownError}
                </div>
            ) : null}

            <div className="card" style={{ marginBottom: 16 }}>
                <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                    <div>
                        <h3 style={{ marginTop: 0, marginBottom: 0 }}>My Linked Calendar</h3>
                        <div style={{ marginTop: 6, fontSize: 13, color: 'var(--muted)' }}>
                            {ownCalendarStatus.connected
                                ? `Connected${ownCalendarStatus.provider ? ` via ${String(ownCalendarStatus.provider).toUpperCase()}` : ''}${ownCalendarStatus.email ? ` as ${ownCalendarStatus.email}` : ''}.`
                                : 'Connect Microsoft 365 or Google Calendar in Settings to see your calendar entries here.'}
                        </div>
                    </div>
                    <Button variant="outlined" size="small" startIcon={<RefreshRoundedIcon />} onClick={loadOwnCalendarEvents} disabled={ownLoading}>
                        {ownLoading ? 'Refreshing…' : 'Refresh'}
                    </Button>
                </div>

                {reminderSync ? (
                    <div style={{ marginTop: 10, fontSize: 12, color: 'var(--muted)' }}>
                        Reminder sync: {reminderSync.total ?? 0} tracked, {reminderSync.created ?? 0} created, {reminderSync.updated ?? 0} updated, {reminderSync.deleted ?? 0} removed.
                    </div>
                ) : null}

                {ownLoading && !ownEvents.length ? (
                    <div style={{ marginTop: 10, fontSize: 13, color: 'var(--muted)' }}>Loading your linked calendar…</div>
                ) : null}

                {ownEvents.length ? (
                    <div style={{ marginTop: 12, overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 760 }}>
                            <thead>
                                <tr>
                                    <th style={{ padding: '10px 12px', borderBottom: '1px solid rgba(15, 23, 42, 0.06)', color: 'var(--muted)', fontWeight: 650, textAlign: 'left' }}>When</th>
                                    <th style={{ padding: '10px 12px', borderBottom: '1px solid rgba(15, 23, 42, 0.06)', color: 'var(--muted)', fontWeight: 650, textAlign: 'left' }}>Time</th>
                                    <th style={{ padding: '10px 12px', borderBottom: '1px solid rgba(15, 23, 42, 0.06)', color: 'var(--muted)', fontWeight: 650, textAlign: 'left' }}>Title</th>
                                    <th style={{ padding: '10px 12px', borderBottom: '1px solid rgba(15, 23, 42, 0.06)', color: 'var(--muted)', fontWeight: 650, textAlign: 'left' }}>Source</th>
                                </tr>
                            </thead>
                            <tbody>
                                {ownEvents.slice(0, 20).map((ev, idx) => (
                                    <tr key={`${ev.provider_event_id || ev.start}-${idx}`} style={{ borderBottom: '1px solid rgba(15, 23, 42, 0.04)' }}>
                                        <td style={{ padding: '10px 12px' }}>{formatDate(ev.start)}</td>
                                        <td style={{ padding: '10px 12px' }}>{formatTimeRange(ev.start, ev.end, ev.is_all_day)}</td>
                                        <td style={{ padding: '10px 12px' }}>
                                            <div style={{ fontWeight: 650 }}>{ev.title || 'Untitled event'}</div>
                                            {ev.is_auto_generated ? (
                                                <div style={{ fontSize: 12, color: 'var(--muted)' }}>Monsurv reminder</div>
                                            ) : null}
                                        </td>
                                        <td style={{ padding: '10px 12px' }}>{String(ev.source || '').toUpperCase()}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        {ownEvents.length > 20 ? (
                            <div style={{ marginTop: 8, fontSize: 12, color: 'var(--muted)' }}>
                                Showing first 20 events in the current calendar window.
                            </div>
                        ) : null}
                    </div>
                ) : null}
            </div>

            {teamError ? (
                <div style={{ marginBottom: 16, padding: 10, borderRadius: 8, background: '#fee2e2', color: '#7f1d1d' }}>
                    {teamError}
                </div>
            ) : null}

            {teamLoading ? (
                <div style={{ marginBottom: 16, fontSize: 13, color: 'var(--muted)' }}>
                    Loading team calendar…
                </div>
            ) : null}

            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                <h3 style={{ marginTop: 0 }}>Calendar</h3>
                {isAdmin ? (
                    <Button variant="outlined" size="small" startIcon={<RefreshRoundedIcon />} onClick={loadSla} disabled={loading}>
                        {loading ? 'Refreshing…' : 'Refresh'}
                    </Button>
                ) : null}
            </div>

            <div className="card" style={{ marginBottom: 16 }}>
                <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                    <h3 style={{ marginTop: 0, marginBottom: 0 }}>SLA Tracking</h3>
                    <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                        <Button variant="outlined" size="small" startIcon={<RefreshRoundedIcon />} onClick={loadTracking} disabled={trackingLoading}>
                            {trackingLoading ? 'Refreshing…' : 'Refresh'}
                        </Button>
                    </div>
                </div>

                <div style={{ marginTop: 6, fontSize: 13, color: 'var(--muted)', lineHeight: 1.45 }}>
                    Initial and Interim Report SLAs run in business days from their Site Visit. Initial SLA pauses are shown separately.
                </div>

                {trackingError ? (
                    <div style={{ marginTop: 10, padding: 10, borderRadius: 8, background: '#fee2e2', color: '#7f1d1d' }}>
                        {trackingError}
                    </div>
                ) : null}

                {trackingLoading && !tracking ? (
                    <div style={{ marginTop: 10, fontSize: 13, color: 'var(--muted)' }}>Loading SLA tracking…</div>
                ) : null}

                {tracking && Array.isArray(tracking.items) ? (
                    <div style={{ marginTop: 10 }}>
                        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', fontSize: 12, color: 'var(--muted)' }}>
                            <div><strong style={{ color: '#111827' }}>{trackingItems.length}</strong> items ({tracking?.window?.days ?? 30} days)</div>
                            <div><strong style={{ color: '#111827' }}>{trackingItems.filter((x) => x.status === 'overdue').length}</strong> overdue</div>
                            <div><strong style={{ color: '#111827' }}>{trackingItems.filter((x) => x.status === 'due_soon').length}</strong> due soon</div>
                            <div><strong style={{ color: '#111827' }}>{trackingItems.filter((x) => x.status === 'late').length}</strong> late</div>
                            <div><strong style={{ color: '#111827' }}>{trackingItems.filter((x) => x.status === 'met').length}</strong> met</div>
                        </div>

                        <div style={{ marginTop: 10, overflowX: 'auto' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 760 }}>
                                <thead>
                                    <tr>
                                        <th style={{ padding: '10px 12px', borderBottom: '1px solid rgba(15, 23, 42, 0.06)', color: 'var(--muted)', fontWeight: 650, textAlign: 'left' }}>
                                            Project
                                        </th>
                                        <th style={{ padding: '10px 12px', borderBottom: '1px solid rgba(15, 23, 42, 0.06)', color: 'var(--muted)', fontWeight: 650, textAlign: 'left' }}>
                                            Type
                                        </th>
                                        <th style={{ padding: '10px 12px', borderBottom: '1px solid rgba(15, 23, 42, 0.06)', color: 'var(--muted)', fontWeight: 650, textAlign: 'left' }}>
                                            Start
                                        </th>
                                        <th style={{ padding: '10px 12px', borderBottom: '1px solid rgba(15, 23, 42, 0.06)', color: 'var(--muted)', fontWeight: 650, textAlign: 'left' }}>
                                            Due
                                        </th>
                                        <th style={{ padding: '10px 12px', borderBottom: '1px solid rgba(15, 23, 42, 0.06)', color: 'var(--muted)', fontWeight: 650, textAlign: 'left' }}>
                                            Status
                                        </th>
                                        <th style={{ padding: '10px 12px', borderBottom: '1px solid rgba(15, 23, 42, 0.06)', color: 'var(--muted)', fontWeight: 650, textAlign: 'right' }}>
                                            Days
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {trackingItems.slice(0, 25).map((it) => (
                                        <tr key={`${it.reference}-${it.report_instance}`}
                                            style={{ borderBottom: '1px solid rgba(15, 23, 42, 0.04)' }}>
                                            <td style={{ padding: '10px 12px' }}>
                                                <div style={{ fontWeight: 650 }}>{it.reference}</div>
                                                <div style={{ fontSize: 12, color: 'var(--muted)' }}>{it.project_name || '—'}</div>
                                            </td>
                                            <td style={{ padding: '10px 12px' }}>
                                                {it.action_type === 'book_site_visit'
                                                    ? `Book ${String(it.report_instance).startsWith('interim:') ? `INT ${it.interim_number}` : 'INI'} Visit`
                                                    : (it.type === 'interim' ? `INT ${it.interim_number}` : 'INI')}
                                            </td>
                                            <td style={{ padding: '10px 12px' }}>{formatDate(it.start_at)}</td>
                                            <td style={{ padding: '10px 12px' }}>{formatDate(it.due_at)}</td>
                                            <td style={{ padding: '10px 12px' }}>{statusChip(it.status)}</td>
                                            <td style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 650 }}>
                                                {it.days_remaining == null ? '—' : it.days_remaining}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {trackingItems.length > 25 ? (
                            <div style={{ marginTop: 8, fontSize: 12, color: 'var(--muted)' }}>
                                Showing first 25 items.
                            </div>
                        ) : null}
                    </div>
                ) : null}
            </div>

            <div className="card" style={{ marginBottom: 16 }}>
                <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                    <h3 style={{ marginTop: 0, marginBottom: 0 }}>Working Week, Business Hours & Holidays</h3>
                    <Button variant="outlined" size="small" startIcon={<RefreshRoundedIcon />} onClick={loadCalendarRules} disabled={rulesLoading}>
                        {rulesLoading ? 'Refreshing…' : 'Refresh'}
                    </Button>
                </div>

                <div style={{ marginTop: 6, fontSize: 13, color: 'var(--muted)', lineHeight: 1.45 }}>
                    These rules are used for business-day SLA mode and to highlight closures/holidays.
                </div>

                {rulesError ? (
                    <div style={{ marginTop: 10, padding: 10, borderRadius: 8, background: '#fee2e2', color: '#7f1d1d' }}>
                        {rulesError}
                    </div>
                ) : null}

                {!rulesDraft ? (
                    <div style={{ marginTop: 10, fontSize: 13, color: 'var(--muted)' }}>
                        {rulesLoading ? 'Loading calendar rules…' : 'No calendar rules loaded.'}
                    </div>
                ) : (
                    <div style={{ marginTop: 12 }}>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 12 }}>
                            <div style={{ border: '1px solid rgba(15, 23, 42, 0.06)', borderRadius: 10, padding: 12 }}>
                                <div style={{ fontWeight: 650 }}>Working Days</div>
                                <div style={{ marginTop: 8, display: 'flex', gap: 10, flexWrap: 'wrap', fontSize: 13 }}>
                                    {[1, 2, 3, 4, 5, 6, 7].map((d) => {
                                        const map = { 1: 'Mon', 2: 'Tue', 3: 'Wed', 4: 'Thu', 5: 'Fri', 6: 'Sat', 7: 'Sun' };
                                        const checked = Array.isArray(rulesDraft.working_days) && rulesDraft.working_days.includes(d);
                                        return (
                                            <label key={d} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                                <input
                                                    type="checkbox"
                                                    disabled={!isAdmin}
                                                    checked={checked}
                                                    onChange={(e) => {
                                                        const next = new Set(Array.isArray(rulesDraft.working_days) ? rulesDraft.working_days : []);
                                                        if (e.target.checked) next.add(d);
                                                        else next.delete(d);
                                                        setRulesDraft({ ...rulesDraft, working_days: Array.from(next).sort((a, b) => a - b) });
                                                    }}
                                                />
                                                {map[d]}
                                            </label>
                                        );
                                    })}
                                </div>
                            </div>

                            <div style={{ border: '1px solid rgba(15, 23, 42, 0.06)', borderRadius: 10, padding: 12 }}>
                                <div style={{ fontWeight: 650 }}>Business Hours</div>
                                <div style={{ marginTop: 10, display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
                                    <label style={{ fontSize: 13, color: 'var(--muted)' }}>
                                        Start{' '}
                                        <input
                                            type="time"
                                            disabled={!isAdmin}
                                            value={rulesDraft?.business_hours?.start || '09:00'}
                                            onChange={(e) => setRulesDraft({
                                                ...rulesDraft,
                                                business_hours: { ...(rulesDraft.business_hours || {}), start: e.target.value }
                                            })}
                                        />
                                    </label>
                                    <label style={{ fontSize: 13, color: 'var(--muted)' }}>
                                        End{' '}
                                        <input
                                            type="time"
                                            disabled={!isAdmin}
                                            value={rulesDraft?.business_hours?.end || '17:30'}
                                            onChange={(e) => setRulesDraft({
                                                ...rulesDraft,
                                                business_hours: { ...(rulesDraft.business_hours || {}), end: e.target.value }
                                            })}
                                        />
                                    </label>
                                </div>

                            </div>
                        </div>

                        <div style={{ marginTop: 14, border: '1px solid rgba(15, 23, 42, 0.06)', borderRadius: 10, padding: 12 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'baseline', flexWrap: 'wrap' }}>
                                <div>
                                    <div style={{ fontWeight: 650 }}>Bank Holidays & Closure Days</div>
                                    <div style={{ marginTop: 6, fontSize: 12, color: 'var(--muted)' }}>
                                        Add dates as YYYY-MM-DD; closures can be used for company shutdown periods.
                                    </div>
                                </div>
                                {isAdmin ? (
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        startIcon={<AddRoundedIcon />}
                                        onClick={() => {
                                            const next = Array.isArray(rulesDraft.holidays) ? [...rulesDraft.holidays] : [];
                                            next.push({ date: '', name: '', kind: 'bank_holiday' });
                                            setRulesDraft({ ...rulesDraft, holidays: next });
                                        }}
                                    >
                                        Add date
                                    </Button>
                                ) : null}

                                <div style={{ border: '1px solid rgba(15, 23, 42, 0.06)', borderRadius: 10, padding: 12 }}>
                                    <div style={{ fontWeight: 650 }}>Automatic Reminder Sync</div>
                                    <div style={{ marginTop: 10, display: 'grid', gap: 10 }}>
                                        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
                                            <input
                                                type="checkbox"
                                                disabled={!isAdmin}
                                                checked={Boolean(rulesDraft?.reminders?.enabled)}
                                                onChange={(e) => setRulesDraft({
                                                    ...rulesDraft,
                                                    reminders: { ...(rulesDraft?.reminders || {}), enabled: e.target.checked }
                                                })}
                                            />
                                            Sync Monsurv SLA reminders into each connected user calendar
                                        </label>

                                        <label style={{ fontSize: 13, color: 'var(--muted)' }}>
                                            Trigger offsets (days before due date, comma-separated)
                                            <input
                                                type="text"
                                                disabled={!isAdmin}
                                                value={Array.isArray(rulesDraft?.reminders?.day_offsets) ? rulesDraft.reminders.day_offsets.join(', ') : '14, 7, 3, 1, 0'}
                                                onChange={(e) => {
                                                    const values = String(e.target.value || '')
                                                        .split(',')
                                                        .map((x) => Number(String(x).trim()))
                                                        .filter((x) => Number.isFinite(x) && x >= 0)
                                                        .map((x) => Math.trunc(x));
                                                    setRulesDraft({
                                                        ...rulesDraft,
                                                        reminders: { ...(rulesDraft?.reminders || {}), day_offsets: values }
                                                    });
                                                }}
                                                style={{ display: 'block', marginTop: 6, width: '100%' }}
                                            />
                                        </label>

                                        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
                                            <label style={{ fontSize: 13, color: 'var(--muted)' }}>
                                                Reminder time{' '}
                                                <input
                                                    type="time"
                                                    disabled={!isAdmin}
                                                    value={rulesDraft?.reminders?.time || '09:00'}
                                                    onChange={(e) => setRulesDraft({
                                                        ...rulesDraft,
                                                        reminders: { ...(rulesDraft?.reminders || {}), time: e.target.value }
                                                    })}
                                                />
                                            </label>
                                            <label style={{ fontSize: 13, color: 'var(--muted)' }}>
                                                Duration (minutes){' '}
                                                <input
                                                    type="number"
                                                    min="5"
                                                    max="480"
                                                    disabled={!isAdmin}
                                                    value={rulesDraft?.reminders?.duration_minutes ?? 30}
                                                    onChange={(e) => setRulesDraft({
                                                        ...rulesDraft,
                                                        reminders: { ...(rulesDraft?.reminders || {}), duration_minutes: Number(e.target.value || 30) }
                                                    })}
                                                    style={{ width: 90 }}
                                                />
                                            </label>
                                            <label style={{ fontSize: 13, color: 'var(--muted)' }}>
                                                Sync window (days){' '}
                                                <input
                                                    type="number"
                                                    min="7"
                                                    max="365"
                                                    disabled={!isAdmin}
                                                    value={rulesDraft?.reminders?.sync_window_days ?? 120}
                                                    onChange={(e) => setRulesDraft({
                                                        ...rulesDraft,
                                                        reminders: { ...(rulesDraft?.reminders || {}), sync_window_days: Number(e.target.value || 120) }
                                                    })}
                                                    style={{ width: 90 }}
                                                />
                                            </label>
                                        </div>

                                        <div style={{ fontSize: 12, color: 'var(--muted)', lineHeight: 1.45 }}>
                                            These reminders are created automatically for connected users to support high-visibility delivery tracking.
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div style={{ marginTop: 10, overflowX: 'auto' }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 640 }}>
                                    <thead>
                                        <tr>
                                            <th style={{ padding: '10px 12px', borderBottom: '1px solid rgba(15, 23, 42, 0.06)', color: 'var(--muted)', fontWeight: 650, textAlign: 'left' }}>
                                                Date
                                            </th>
                                            <th style={{ padding: '10px 12px', borderBottom: '1px solid rgba(15, 23, 42, 0.06)', color: 'var(--muted)', fontWeight: 650, textAlign: 'left' }}>
                                                Name
                                            </th>
                                            <th style={{ padding: '10px 12px', borderBottom: '1px solid rgba(15, 23, 42, 0.06)', color: 'var(--muted)', fontWeight: 650, textAlign: 'left' }}>
                                                Type
                                            </th>
                                            <th style={{ padding: '10px 12px', borderBottom: '1px solid rgba(15, 23, 42, 0.06)', color: 'var(--muted)', fontWeight: 650, textAlign: 'right' }}>

                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {(Array.isArray(rulesDraft.holidays) ? rulesDraft.holidays : []).map((h, idx) => (
                                            <tr key={`${idx}-${h?.date || ''}`} style={{ borderBottom: '1px solid rgba(15, 23, 42, 0.04)' }}>
                                                <td style={{ padding: '10px 12px' }}>
                                                    <input
                                                        type="date"
                                                        disabled={!isAdmin}
                                                        value={h?.date || ''}
                                                        onChange={(e) => {
                                                            const next = [...(rulesDraft.holidays || [])];
                                                            next[idx] = { ...(next[idx] || {}), date: e.target.value };
                                                            setRulesDraft({ ...rulesDraft, holidays: next });
                                                        }}
                                                    />
                                                </td>
                                                <td style={{ padding: '10px 12px' }}>
                                                    <input
                                                        type="text"
                                                        disabled={!isAdmin}
                                                        value={h?.name || ''}
                                                        placeholder="e.g. Christmas Day"
                                                        onChange={(e) => {
                                                            const next = [...(rulesDraft.holidays || [])];
                                                            next[idx] = { ...(next[idx] || {}), name: e.target.value };
                                                            setRulesDraft({ ...rulesDraft, holidays: next });
                                                        }}
                                                        style={{ width: '100%', minWidth: 240 }}
                                                    />
                                                </td>
                                                <td style={{ padding: '10px 12px' }}>
                                                    <select
                                                        disabled={!isAdmin}
                                                        value={h?.kind || 'bank_holiday'}
                                                        onChange={(e) => {
                                                            const next = [...(rulesDraft.holidays || [])];
                                                            next[idx] = { ...(next[idx] || {}), kind: e.target.value };
                                                            setRulesDraft({ ...rulesDraft, holidays: next });
                                                        }}
                                                    >
                                                        <option value="bank_holiday">Bank holiday</option>
                                                        <option value="closure">Closure</option>
                                                        <option value="other">Other</option>
                                                    </select>
                                                </td>
                                                <td style={{ padding: '10px 12px', textAlign: 'right' }}>
                                                    {isAdmin ? (
                                                        <Button
                                                            color="error"
                                                            size="small"
                                                            startIcon={<DeleteOutlineRoundedIcon />}
                                                            onClick={() => {
                                                                const next = [...(rulesDraft.holidays || [])];
                                                                next.splice(idx, 1);
                                                                setRulesDraft({ ...rulesDraft, holidays: next });
                                                            }}
                                                        >
                                                            Remove
                                                        </Button>
                                                    ) : null}
                                                </td>
                                            </tr>
                                        ))}

                                        {(!rulesDraft.holidays || rulesDraft.holidays.length === 0) ? (
                                            <tr>
                                                <td colSpan={4} style={{ padding: '10px 12px', color: 'var(--muted)', fontSize: 13 }}>
                                                    No holidays/closures configured.
                                                </td>
                                            </tr>
                                        ) : null}
                                    </tbody>
                                </table>
                            </div>

                            {isAdmin ? (
                                <div style={{ marginTop: 12, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                                    <Button variant="contained" startIcon={<SaveRoundedIcon />} onClick={() => saveCalendarRules(rulesDraft)} disabled={rulesSaving}>
                                        {rulesSaving ? 'Saving…' : 'Save calendar rules'}
                                    </Button>
                                    <Button variant="outlined" startIcon={<RestartAltRoundedIcon />} onClick={() => setRulesDraft(rules)} disabled={rulesSaving}>
                                        Reset
                                    </Button>
                                </div>
                            ) : (
                                <div style={{ marginTop: 12, fontSize: 12, color: 'var(--muted)' }}>
                                    Only Admin users can edit calendar rules.
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>

            <div className="card" style={{ marginBottom: 16 }}>
                <h3 style={{ marginTop: 0 }}>Service Level Agreement (SLA)</h3>

                {!isAdmin ? (
                    <div style={{ fontSize: 13, color: 'var(--muted)' }}>
                        SLA settings are managed by Admin users.
                    </div>
                ) : null}

                {error ? (
                    <div style={{ marginTop: 10, padding: 10, borderRadius: 8, background: '#fee2e2', color: '#7f1d1d' }}>
                        {error}
                    </div>
                ) : null}

                {isAdmin ? (
                    loading && !sla ? (
                        <div style={{ marginTop: 8, fontSize: 13, color: 'var(--muted)' }}>Loading SLA…</div>
                    ) : (
                        <div style={{ marginTop: 10 }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                <thead>
                                    <tr>
                                        <th style={{ padding: '10px 12px', borderBottom: '1px solid rgba(15, 23, 42, 0.06)', color: 'var(--muted)', fontWeight: 650, textAlign: 'left' }}>
                                            Policy
                                        </th>
                                        <th style={{ padding: '10px 12px', borderBottom: '1px solid rgba(15, 23, 42, 0.06)', color: 'var(--muted)', fontWeight: 650, textAlign: 'right' }}>
                                            Days
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td style={{ padding: '10px 12px', borderBottom: '1px solid rgba(15, 23, 42, 0.04)' }}>
                                            Initial Report Turnaround Time
                                        </td>
                                        <td style={{ padding: '10px 12px', borderBottom: '1px solid rgba(15, 23, 42, 0.04)', textAlign: 'right', fontWeight: 650 }}>
                                            {sla?.initial_report_turnaround_days ?? '—'}
                                        </td>
                                    </tr>
                                    <tr>
                                        <td style={{ padding: '10px 12px', borderBottom: '1px solid rgba(15, 23, 42, 0.04)' }}>
                                            Interim Report Turnaround Time
                                        </td>
                                        <td style={{ padding: '10px 12px', borderBottom: '1px solid rgba(15, 23, 42, 0.04)', textAlign: 'right', fontWeight: 650 }}>
                                            {sla?.interim_report_turnaround_days ?? '—'}
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    )
                ) : null}
            </div>

            <div className="card">
                <h3 style={{ marginTop: 0 }}>Calendar Notes</h3>
                <div style={{ fontSize: 13, color: 'var(--muted)' }}>
                    Use the month view above to see shared team availability and SLA deadlines in one place.
                </div>
            </div>
        </div>
    );
}
